/**
 * A line that opens somewhere outside the app.
 *
 * The address and the number appear on two screens — the menu, where a customer
 * decides whether to come, and the tracking card, where they are on their way —
 * and they behave the same on both, so the row lives here rather than twice.
 */

import { Linking, Pressable } from 'react-native';
import { cn, Icon, Text, useResponsive } from '@yummy/ui-rnr';
import type { LucideIcon } from 'lucide-react-native';

export interface ContactRowProps {
  /** The glyph that says what the line is before the text does. */
  icon: LucideIcon;
  /** What a screen reader reads, in place of the bare address or number. */
  label: string;
  /** The address or the number, as the restaurant stored it. */
  value: string;
  /** Where the tap goes: a Maps URL or a `tel:` URI. */
  url: string;
  /**
   * Which scale the row sits in.
   *
   * `sm` is the menu header, where every line around it — the hours, the
   * description — is 14. `base` is a card, where the content it sits among is
   * 16: at 14 beside 16pt lines these were the smallest text on a screen a
   * customer reads while walking, which is the wrong place to whisper.
   */
  size?: 'sm' | 'base';
  /**
   * How the line is coloured.
   *
   * `link` is the menu header's: there the colour is what says the line opens
   * somewhere else, beside hours and a description that are all plain text.
   * `plain` is the tracking card's, where the address and the number sit with
   * ordinary card content: both take the quiet tier, the same colour as their
   * own glyph, so they read as one line of detail rather than as a heading. The
   * tap still says it opens; nothing is lost.
   */
  variant?: 'link' | 'plain';
}

/**
 * Opens a URL in the browsing context this app asks for.
 *
 * React Native types `Linking.openURL` with a single argument; the web build
 * takes a second one for the target. That second argument is the whole point
 * here, so the cast is made once, in the open, rather than worked around by
 * reaching for `window` and losing the `tel:` handling the web build does.
 *
 * @param href The URL to open.
 * @param target `_self` to replace this page, `_blank` to open beside it.
 */
export function openContactUrl(href: string, target: '_self' | '_blank'): void {
  void (Linking.openURL as (url: string, where?: string) => Promise<unknown>)(href, target);
}

/**
 * Renders one tappable line.
 *
 * The whole row is the target rather than the words in it, because a phone
 * number is nine characters of tap area otherwise and the row above it is aiming
 * at the same column — `py-1` is what makes the row taller than its text. The
 * text is capped at two lines: an address column allows 300 characters, and an
 * uncapped one buries everything under it.
 *
 * A phone opens the URL in place, so the operating system takes it and there is
 * no tab left behind; anywhere else it opens beside the app, because navigating
 * away would lose the screen the customer was reading.
 *
 * @param props The icon, the spoken label, the text, and where it goes.
 * @returns The tappable row.
 */
export function ContactRow({
  icon,
  label,
  value,
  url,
  size = 'sm',
  variant = 'link',
}: ContactRowProps) {
  const { breakpoint } = useResponsive();
  // A phone can hand the URL to the app that owns it, so the tap replaces this
  // page and leaves no tab behind it. Anywhere else Maps opens beside the app,
  // because navigating away would lose the screen the customer was reading.
  const target = breakpoint === 'phone' ? '_self' : '_blank';
  const base = size === 'base';
  const link = variant === 'link';

  return (
    <Pressable
      onPress={() => openContactUrl(url, target)}
      accessibilityRole="link"
      accessibilityLabel={label}
      className="flex-row items-start gap-2 py-1 active:opacity-70">
      <Icon
        as={icon}
        size={base ? 16 : 15}
        // Centred on the text's *first line*, not on the row: a two-line address
        // would otherwise drag the icon down to the middle of the paragraph.
        // `items-start` puts both boxes at the same top, so the icon needs the
        // difference between the line box and its own height as a top margin —
        // (24 − 16) / 2 = 4 at base, (20 − 15) / 2 = 2.5 at sm.
        className={cn(link ? 'text-primary' : 'text-muted-foreground', base ? 'mt-1' : 'mt-0.5')}
      />
      <Text
        numberOfLines={2}
        className={cn(
          // Plain rows are the same colour as their glyph: the address and the
          // number are one quiet line of detail, not two pieces of prose, and at
          // the foreground they were the darkest text in a card of labels.
          'min-w-0 flex-1',
          link ? 'text-primary' : 'text-muted-foreground',
          // 15/20, which is between the scale's 14 and 16. The line-height is
          // explicit because an arbitrary size carries none, and the icon's
          // top margin below is the difference between it and the icon.
          base ? 'text-base font-medium' : 'text-[15px]/5'
        )}>
        {value}
      </Text>
    </Pressable>
  );
}
